import { Box, InputAdornment, Switch, TextField, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import { Controller } from 'react-hook-form'
import { animalAdoptionOfferTypeOrder, getReadableAnimalAdoptionOfferType } from '../../../domain/Animal/AnimalDisplay'
import { AnimalAdoptionDtoOfferType } from '../../../interactors/gen/backendClient'
import { isValidPastDateCoherence } from '../../../utils/date/isValidPastDateCoherence'
import { ColoredChipSelectField } from '../../common/ColoredChipSelectField'
import { ControlledDateField } from '../../common/ControlledDateField'
import { ControlledSelectField } from '../../common/ControlledSelectField'
import { blue, theme } from '../../theme'
import { StepsProps } from './StepOne'

export const StepThreeAdoptionInfo: React.FC<StepsProps> = ({ control, watch, setValue, register, errors }) => {
  const offerType = watch('offerType')
  const hasDeposit = watch('hasDeposit')
  const isDonation = offerType === AnimalAdoptionDtoOfferType.Donation
  const isFreeAmount = offerType === AnimalAdoptionDtoOfferType.FreeAmount

  useEffect(() => {
    if (!offerType) {
      setValue('offerType', AnimalAdoptionDtoOfferType.HealthCostCompensation)
    }
  }, [offerType, setValue])

  useEffect(() => {
    if (isDonation) {
      setValue('priceInEuros', 0)
    }
  }, [isDonation, setValue])

  useEffect(() => {
    const priceInEuros = watch('priceInEuros')
    if (isFreeAmount && (priceInEuros === undefined || priceInEuros === null || Number(priceInEuros) < 1)) {
      setValue('priceInEuros', 1, { shouldValidate: true })
    }
  }, [isFreeAmount, setValue, watch])

  useEffect(() => {
    if (!hasDeposit) {
      setValue('depositDate', undefined)
      setValue('depositAmount', undefined)
      setValue('depositStatus', 'true')
    }
  }, [hasDeposit, setValue])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
      <Box>
        <Typography variant="body2" sx={{ color: blue, fontSize: 20, fontWeight: 700, mb: 1 }}>
          Configurez la nouvelle adoption
        </Typography>
        <ControlledSelectField
          control={control}
          error={errors.offerType}
          fieldName="offerType"
          label="Nature de l'offre"
          options={animalAdoptionOfferTypeOrder.map((field: AnimalAdoptionDtoOfferType) => ({
            label: getReadableAnimalAdoptionOfferType(field),
            value: field,
          }))}
          requiredRule="Vous devez renseigner la nature de l'offre."
          size="small"
        />
        <TextField
          id="price-input"
          label={isFreeAmount ? 'Montant minimum (min. 1€)' : "Montant de l'adoption"}
          type="number"
          inputProps={{
            step: '0.01',
            inputMode: 'decimal',
            pattern: '[0-9]*',
            min: isFreeAmount ? 1 : 0,
          }}
          fullWidth
          {...register('priceInEuros', {
            valueAsNumber: true,
            validate: (value) => {
              if (isDonation) return true
              if (isFreeAmount && (value === undefined || value === null || Number.isNaN(value))) {
                return 'Le montant minimum est requis'
              }
              if (isFreeAmount && Number(value) < 1) {
                return 'Le montant minimum doit être au moins de 1€'
              }
              if (value === undefined || value === null || Number.isNaN(value)) {
                return "Le montant de l'adoption est requis"
              }
              if (value < 0) {
                return 'Le montant doit être positif'
              }
              return true
            },
          })}
          InputProps={{
            endAdornment: <InputAdornment position="end">€</InputAdornment>,
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ mt: 2 }}
          size="small"
          disabled={isDonation}
          error={!!errors.priceInEuros}
          helperText={errors.priceInEuros?.message}
        />
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography fontWeight={600}>Un acompte a-t-il déjà été réalisé ?</Typography>
          <Controller
            name="hasDeposit"
            control={control}
            render={({ field }) => (
              <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} color="primary" />
            )}
          />
        </Box>
        {hasDeposit && (
          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            <ControlledDateField
              control={control}
              error={errors.depositDate}
              fieldName="depositDate"
              label="Date de l'acompte"
              requiredRule="Vous devez renseigner la date de l'acompte."
              size="small"
              validate={(value) => isValidPastDateCoherence(value) || value === null}
            />
            <TextField
              id="deposit-amount-input"
              label="Montant de l'acompte"
              type="number"
              inputProps={{
                step: '0.01',
                inputMode: 'decimal',
                pattern: '[0-9]*',
              }}
              fullWidth
              {...register('depositAmount', {
                valueAsNumber: true,
                validate: (value) => {
                  if (!hasDeposit) return true
                  if (value === undefined || value === null || Number.isNaN(value)) {
                    return "Le montant de l'acompte est requis"
                  }
                  if (value < 0) {
                    return 'Le montant doit être positif'
                  }
                  return true
                },
              })}
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
              error={!!errors.depositAmount}
              helperText={errors.depositAmount?.message}
            />
            <ColoredChipSelectField
              control={control}
              error={errors.depositStatus}
              fieldName="depositStatus"
              label="État de l'acompte"
              options={[
                { label: 'Validé', value: 'true', color: theme.palette.success.main },
                { label: 'En attente', value: 'false', color: theme.palette.warning.main },
              ]}
              requiredRule=""
              size="small"
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}
